import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Providers } from "@tripian/model";
import moment from "moment";
import useVictoryInfo from "../../../hooks/useVictoryInfo";
import { GoogleMapsProductInfo, PreLoading, SvgIcons } from "@tripian/react";
import TicketModal from "./TicketModal/TicketModal";
import useTranslate from "../../../hooks/useTranslate";
import * as gtag from "../../../gtag";
interface IVictoryProductInfo {
  productId: string;
  isOpen: boolean;
  onClose: () => void;
}

const VictoryProductInfo: React.FC<IVictoryProductInfo> = ({
  productId,
  isOpen,
  onClose,
}) => {
  const {
    fetchEventDetails,
    fetchTicketGroups,
    loadingEventDetails,
    eventDetails,
    loadingTicketGroups,
    ticketGroups,
  } = useVictoryInfo();
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );

  const { t } = useTranslate();

  useEffect(() => {
    if (isOpen) {
      fetchEventDetails(productId);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (eventDetails) {
      fetchTicketGroups(productId);
    }
  }, [eventDetails, fetchTicketGroups, productId]);

  useEffect(() => {
    const container = document.createElement("div");
    container.id = "ticket-modal-portal";
    document.body.appendChild(container);
    setPortalContainer(container);

    return () => {
      try {
        document.body.removeChild(container);
      } catch (error){
        console.info("VProduct");
      }
    };
  }, []);

  const formatDate = (dateStr: string) =>
    moment(dateStr).format("ddd, MMM D, h:mm A");

  if (loadingEventDetails) {
    return (
      <div className="fixed inset-0 bg-white z-[1100] flex items-center justify-center">
        <PreLoading />
      </div>
    );
  }
  if (!eventDetails) {
    return (
      <>
        <div className="mt-12 mx-auto p-4 text-center">
          Your platform does not support "Victory" tours.
        </div>
      </>
    );
  }

  const center = {
    lat: eventDetails.venue.address.latitude,
    lng: eventDetails.venue.address.longitude,
  };

  const artists = eventDetails.performances.map(
    pr => (pr as unknown as Providers.Victory.Performance).performer?.name || ""
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[1100] overflow-y-auto">
      <div className="min-h-screen bg-white relative mt-[60px] md:mt-[90px]">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="relative bg-white rounded-xl shadow-lg overflow-hidden">
            <button
              className="absolute z-10 top-4 left-4 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
              onClick={() => onClose()}
            >
              <SvgIcons.ArrowLeft className="w-6 h-6" />
            </button>
            <div className="h-64 bg-gradient-to-r from-primary-color to-warning-color flex items-center justify-center p-8">
              <h1 className="text-4xl font-bold text-white text-center">
                {eventDetails.name}
              </h1>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      {t("category")}
                    </h3>
                    <p className="mt-1">
                      <span className="text-gray-600">{t("theatre")} / </span>
                      <span>{t("entertainmentShows")}</span>
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      {t("dateAndTime")}
                    </h3>
                    <p className="mt-1">
                      {formatDate(eventDetails.occurs_at_local)}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      {t("place")}
                    </h3>
                    <p className="mt-1 font-medium">
                      {eventDetails.venue.name}
                    </p>
                    <p className="text-gray-600">
                      {eventDetails.venue.address.street_address}
                      <br />
                      {eventDetails.venue.address.location},{" "}
                      {eventDetails.venue.address.postal_code}
                      <br />
                      {eventDetails.venue.address.country_code}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      {t("performers")}
                    </h3>
                    <div className="mt-1 space-y-1">
                      <p className="text-blue-600 font-medium">
                        {artists.join(", ")}
                      </p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      {t("availableTicket")}
                    </h3>
                    <p className="mt-1">{eventDetails.available_count}</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg">
                  <GoogleMapsProductInfo center={center} zoom={15} />
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <button
                  className={`px-8 py-3 rounded-lg transition-colors flex items-center min-w-[16.75rem] ${
                    loadingTicketGroups
                      ? "bg-primary-color-900 text-white cursor-not-allowed"
                      : !ticketGroups || ticketGroups.length === 0
                        ? "bg-primary-color-900 text-white cursor-not-allowed"
                        : "bg-primary-color text-white hover:bg-primary-color/90"
                  }`}
                  onClick={() => {
                    if (!loadingTicketGroups && ticketGroups && ticketGroups.length > 0) {
                      gtag.googleAnalyticsEvent("tour_book_click", { provider: "victory", tour_id: productId });
                      setIsTicketModalOpen(true);
                    }
                  }}
                  disabled={
                    loadingTicketGroups ||
                    !ticketGroups ||
                    ticketGroups.length === 0
                  }
                >
                  {loadingTicketGroups ? (
                    <>
                      <PreLoading size="small" color="white" />
                    </>
                  ) : (
                    <>
                      <SvgIcons.Ticket2 className="w-5 h-5 mr-2" />
                      {ticketGroups && ticketGroups.length > 0
                        ? t("checkAvailableTickets")
                        : t("noTicketsAvailable")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {ticketGroups && ticketGroups.length > 0 && portalContainer
            ? createPortal(
                <TicketModal
                  isOpen={isTicketModalOpen}
                  onClose={() => setIsTicketModalOpen(false)}
                  tickets={ticketGroups}
                  eventId={eventDetails.id}
                  venueId={eventDetails.venue.id.toString()}
                  configurationId={eventDetails.configuration.id.toString()}
                  t={t}
                />,
                portalContainer
              )
            : null}
        </div>
      </div>
    </div>
  );
};

export default VictoryProductInfo;
